import { GroupsOutlined as TeamManagementIcon } from '@mui/icons-material';
import type { FunctionComponent, ReactNode } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { IconJobArchitecture, IconSkills } from '~/ui/core/icons';

import { SideDrawerLinkV2 } from '../app/app-bar/menu-bar-v2/SideDrawerLinkV2';
import { useJobArchitectureOptionsQuery } from '../app/query-hooks/job-architecture-options';
import { useSkillCatalogOptions } from './api/use-skill-catalog-options';
import { useResolveTargetArchitectureId } from './select-page/use-resolve-target-architecture-id';

const useCanUserGetJobArchitecture = (): boolean => {
    const jobArchitectureOptions = useJobArchitectureOptionsQuery();
    return jobArchitectureOptions.isSuccess && jobArchitectureOptions.data.isOk
        ? jobArchitectureOptions.data.value.canUserGetJobArchitecture
        : false;
};

export const useSkillsListItem = (): ReactNode => {
    const {
        applicationState: { featureFlagService },
    } = useApplicationContext();
    const canUserGetJobArchitecture = useCanUserGetJobArchitecture();
    const { data: skillCatalogOptions } = useSkillCatalogOptions();
    const translate = useTranslate();

    if (
        !canUserGetJobArchitecture ||
        !featureFlagService.isFeatureFlagEnabled('skills-section') ||
        skillCatalogOptions?.canReadSkillCatalog !== true
    ) {
        return null;
    }

    return (
        <SideDrawerLinkV2
            externalUrl={false}
            Icon={TeamManagementIcon}
            label={translate('navigation.skills')}
            NewIcon={IconSkills}
            path="/skills"
        />
    );
};

const SideDrawerLinkJobArchitecture: FunctionComponent = () => {
    const targetArchitectureId = useResolveTargetArchitectureId();
    const translate = useTranslate();

    return (
        <SideDrawerLinkV2
            externalUrl={false}
            Icon={TeamManagementIcon}
            label={translate('navigation.job-architecture')}
            NewIcon={IconJobArchitecture}
            path={`/job-architecture-v2${targetArchitectureId ? `/${targetArchitectureId}` : ''}`}
        />
    );
};

export const useJobArchitectureListItem = (): ReactNode => {
    const canUserGetJobArchitecture = useCanUserGetJobArchitecture();

    if (!canUserGetJobArchitecture) {
        return null;
    }

    return <SideDrawerLinkJobArchitecture />;
};
